import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Container } from '../../styles/GlobalStyles';
import Logo from '../Logo/Logo';

const FooterContainer = styled.footer`
  background: ${theme.colors.primary.data};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing['4xl']} 0 ${theme.spacing.xl} 0;
  position: relative;
  overflow: hidden;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: ${theme.spacing['2xl']};
  margin-bottom: ${theme.spacing['2xl']};
  position: relative;
  z-index: 2;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    gap: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }
`;

const FooterSection = styled.div``;

const FooterTitle = styled.h3`
  color: ${theme.colors.neutral.white};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.lg};
`;

const SolutionsTitle = styled(FooterTitle)`
  .highlight {
    text-decoration: underline;
    text-decoration-color: ${theme.colors.primary.synapse};
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
  }
`;

const CompanyTitle = styled(FooterTitle)`
  .highlight {
    text-decoration: underline;
    text-decoration-color: ${theme.colors.primary.accent};
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
  }
`;

const ContactTitle = styled(FooterTitle)`
  .highlight {
    text-decoration: underline;
    text-decoration-color: ${theme.colors.neutral.white};
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.neutral.white};

  svg {
    height: 34px;
  }
`;

const FooterDescription = styled.p`
  color: ${theme.colors.neutral.gray300};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing.lg};
`;

const FooterList = styled.ul`
  list-style: none;
`;

const FooterListItem = styled.li`
  margin-bottom: ${theme.spacing.sm};
`;

const FooterLink = styled.a`
  color: ${theme.colors.neutral.gray300};
  transition: color ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;

  &:hover {
    color: ${theme.colors.primary.synapse};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    fill: ${theme.colors.neutral.white};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    justify-content: flex-start;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: ${theme.colors.neutral.white};
  transition: all ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.primary.laa};
    transform: translateY(-2px);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: ${theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: ${theme.colors.neutral.gray400};
  font-size: ${theme.typography.fontSize.sm};
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const FooterBottomLink = styled.a`
  color: ${theme.colors.neutral.gray400};
  font-size: ${theme.typography.fontSize.sm};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary.laa};
  }
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          {/* Informações da Empresa */}
          <FooterSection>
            <FooterLogo>
              <Logo />
            </FooterLogo>
            
            <FooterDescription>
              Conectamos dados a decisões estratégicas por meio de agentes inteligentes, 
              modelos de IA sob medida e engenharia de dados, com foco em governança e LGPD.
            </FooterDescription>

            <SocialLinks>
              <SocialLink href="https://linkedin.com/company/datasynapse" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </SocialLink>
              <SocialLink href="https://github.com/datasynapse" aria-label="GitHub">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </SocialLink>
              <SocialLink href="https://instagram.com/datasynapse" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          {/* Soluções */}
          <FooterSection>
            <SolutionsTitle>
              <span className="highlight">Sol</span>uções
            </SolutionsTitle>
            <FooterList>
              <FooterListItem>
                <FooterLink href="#agentes">Agentes & Automações Inteligentes</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#modelos-ia">Modelos de IA Sob Medida</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#consultoria">Consultoria em IA & Engenharia de Dados</FooterLink>
              </FooterListItem>
            </FooterList>
          </FooterSection>

          {/* Empresa */}
          <FooterSection>
            <CompanyTitle>
              <span className="highlight">Emp</span>resa
            </CompanyTitle>
            <FooterList>
              <FooterListItem>
                <FooterLink href="#sobre">Sobre Nós</FooterLink>
              </FooterListItem>
            </FooterList>
          </FooterSection>

          {/* Contato */}
          <FooterSection>
            <ContactTitle>
              <span className="highlight">Con</span>tato
            </ContactTitle>
            <FooterList>
              <FooterListItem>
                <FooterLink href="mailto:comercial@datasynapse.ai">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  comercial@datasynapse.ai
                </FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#endereco">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  Belo Horizonte, MG - Brasil
                </FooterLink>
              </FooterListItem>
            </FooterList>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © {currentYear} dataSynapse. Todos os direitos reservados.
          </Copyright>
          
          <FooterBottomLinks>
            <FooterBottomLink href="#privacidade">
              Política de Privacidade
            </FooterBottomLink>
            <FooterBottomLink href="#termos">
              Termos de Uso
            </FooterBottomLink>
            <FooterBottomLink href="#cookies">
              Cookies
            </FooterBottomLink>
          </FooterBottomLinks>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer; 